import {
  ArrowUpRight,
  CircleCheckBig,
  CloudUpload,
  CopyCheck,
  Database,
  FileAudio,
  FileText,
  FolderOpen,
  PenLine,
  Play,
  StickyNote,
  TriangleAlert,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { CaptureRoute } from "@/app/surfaces";
import { useApp } from "@/lib/AppContext";
import {
  ApiError,
  DuplicateUploadError,
  UploadCancelled,
  type DuplicateSource,
  type SourceMaterial,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatBytes, formatDate, today } from "@/lib/format";

/**
 * CAP-1, CAP-2, CAP-3 — the two capture routes, which differ only in their copy and
 * their accepted formats. One component, because two would drift.
 *
 * There is no recording control anywhere on this page, and there is no route that
 * offers one: CAP-1 is explicit that in-app recording was tried and is not used.
 * Audio arrives as a file from a voice recorder.
 *
 * The file is chosen first and uploaded when the owner presses the button, rather than
 * starting the moment it is dropped. That differs from the prototype's timing and is
 * deliberate — the duplicate answer comes from the Server's hash of the whole file, so
 * there is nothing useful to say until the bytes are up, and staging a four-gigabyte
 * upload the owner might still cancel is a lot of disk to spend on a maybe.
 */
export function UploadSourcePage({ capture }: { capture: CaptureRoute }) {
  const spec = capture.upload!;
  const { api, me, refreshRecentSources, refreshRecordings } = useApp();

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [sourceDate, setSourceDate] = useState(today());
  const [language, setLanguage] = useState(me.settings?.defaultLanguage ?? "auto");
  const [expectedSpeakers, setExpectedSpeakers] = useState("auto");
  const [authoredByMe, setAuthoredByMe] = useState(false);

  /** A fraction while an upload is in flight, null otherwise. */
  const [progress, setProgress] = useState<number | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateSource | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [added, setAdded] = useState<SourceMaterial | null>(null);

  const upload = useRef<AbortController | null>(null);
  const uploading = progress !== null;

  // Navigating away mid-upload abandons the transfer rather than letting the request
  // run on, reporting progress into a component that no longer exists.
  useEffect(() => () => upload.current?.abort(), []);

  const choose = useCallback(
    (chosen: File | null) => {
      if (!chosen) return;

      setDuplicate(null);
      setAdded(null);

      const dot = chosen.name.lastIndexOf(".");
      const extension = dot >= 0 ? chosen.name.slice(dot).toLowerCase() : "";
      if (!spec.extensions.includes(extension)) {
        // Refused here as well as at the Server. The picker's `accept` list already
        // hides these, but a dropped file bypasses it, and letting four gigabytes of
        // video upload only to be refused at the far end would be worse than rude.
        setFile(null);
        setProblem(
          extension
            ? `${extension} files are not accepted. ${spec.acceptedSentence}${explainOmission(extension)}`
            : `That file has no extension, so there is no way to tell what it is. ${spec.acceptedSentence}`,
        );
        return;
      }

      if (chosen.size > spec.maxBytes) {
        setFile(null);
        setProblem(`That file is ${formatBytes(chosen.size)}. ${spec.formatNote}`);
        return;
      }

      setProblem(null);
      setFile(chosen);
      setTitle(chosen.name.replace(/\.[^.]+$/, ""));
    },
    [spec],
  );

  const clear = useCallback(() => {
    upload.current?.abort();
    upload.current = null;
    setFile(null);
    setProgress(null);
    setDuplicate(null);
    setProblem(null);
    setAuthoredByMe(false);
  }, []);

  const submit = useCallback(async () => {
    if (!file) return;

    const controller = new AbortController();
    upload.current = controller;

    setProgress(0);
    setDuplicate(null);
    setProblem(null);

    try {
      const created = await api.upload(
        {
          kind: spec.kind,
          file,
          title,
          sourceDate,
          language,
          expectedSpeakers,
          authoredByMe: spec.kind === "document" && authoredByMe,
        },
        { onProgress: setProgress, signal: controller.signal },
      );

      setAdded(created);
      setFile(null);
      setProgress(null);
      setAuthoredByMe(false);
      // The panel's "Recently added" is the receipt for this, so it must not lag
      // behind the thing that just happened — and so is the recordings list, which the
      // upload has just put a queued row on (UI-4).
      refreshRecentSources();
      refreshRecordings();
    } catch (cause) {
      setProgress(null);

      if (cause instanceof DuplicateUploadError) setDuplicate(cause.duplicate);
      else if (cause instanceof UploadCancelled) setProblem(null);
      else if (cause instanceof ApiError) setProblem(cause.message);
      else setProblem("The upload could not be completed.");
    } finally {
      upload.current = null;
    }
  }, [
    api,
    authoredByMe,
    expectedSpeakers,
    file,
    language,
    refreshRecentSources,
    refreshRecordings,
    sourceDate,
    spec.kind,
    title,
  ]);

  return (
    <div className="flex max-w-[940px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-wrap items-baseline gap-[var(--space-4)]">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          {capture.label}
        </h1>
        <p className="max-w-[64ch] text-[length:var(--text-md)] text-[var(--text-secondary)]">{spec.subtitle}</p>
      </header>

      {added && <AddedPanel source={added} />}

      {file ? (
        <StagedFileCard
          file={file}
          kind={spec.kind}
          progress={progress}
          onCancel={clear}
        />
      ) : (
        <DropZone spec={spec} onChoose={choose} disabled={uploading} />
      )}

      {duplicate && <DuplicatePanel duplicate={duplicate} onSkip={clear} />}

      {problem && (
        <p
          role="alert"
          className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-lg)] border border-[var(--destructive)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]"
          style={{ background: "color-mix(in oklch, var(--destructive) 10%, transparent)" }}
        >
          <TriangleAlert size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-[var(--destructive)]" />
          {problem}
        </p>
      )}

      {file && (
        <div className="flex flex-col gap-[var(--space-4)] rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
          <div className="flex flex-wrap gap-[var(--space-4)]">
            <Field label="Title" className="min-w-[240px] flex-[2]">
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={uploading}
                className={inputClass}
              />
            </Field>

            <Field label={spec.dateLabel} className="min-w-[160px] flex-1">
              <input
                type="date"
                value={sourceDate}
                onChange={(event) => setSourceDate(event.target.value)}
                disabled={uploading}
                className={inputClass}
              />
            </Field>

            <Field label="Spoken language" className="min-w-[180px] flex-1">
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                disabled={uploading}
                className={inputClass}
              >
                <option className={optionClass} value="auto">
                  Auto-detect
                </option>
                <option className={optionClass} value="nl">
                  Nederlands
                </option>
                <option className={optionClass} value="en">
                  English
                </option>
              </select>
            </Field>

            {/* SPCH-3's hint, and only on the audio route: a document has nobody
                speaking in it, and a control that quietly does nothing is worse than
                one that is not there. Auto is the default and stays the honest answer —
                the diarizer is *made* to find exactly the number it is given, so a
                wrong count is worse than no count. */}
            {spec.kind === "recording" && (
              <Field label="Expected speakers" className="min-w-[160px] flex-1">
                <select
                  value={expectedSpeakers}
                  onChange={(event) => setExpectedSpeakers(event.target.value)}
                  disabled={uploading}
                  className={inputClass}
                >
                  <option className={optionClass} value="auto">
                    Auto-detect
                  </option>
                  {SPEAKER_COUNTS.map((count) => (
                    <option key={count} className={optionClass} value={count}>
                      {count}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>

          {/* CAP-8 — only on the document route: a recording is a meeting other people spoke in. */}
          {spec.kind === "document" && (
            <AuthoredByMe checked={authoredByMe} onChange={setAuthoredByMe} disabled={uploading} noun="document" />
          )}

          <div className="flex flex-wrap items-center justify-end gap-[var(--space-4)]">
            <p className="mr-auto text-[length:var(--text-xs)] text-[var(--text-muted)]">{spec.formatNote}</p>

            <button type="button" onClick={clear} className={ghostButtonClass}>
              Cancel
            </button>

            <button
              type="button"
              onClick={() => void submit()}
              disabled={uploading || duplicate !== null}
              className={primaryButtonClass}
            >
              {spec.kind === "recording" ? <Play size={14} strokeWidth={2.5} /> : <Database size={14} strokeWidth={2.5} />}
              {uploading ? "Uploading…" : spec.submitLabel}
            </button>
          </div>
        </div>
      )}

      <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
        {spec.kind === "recording"
          ? "The file is stored exactly as it arrived and queued for the worker, which transcribes and diarizes it on your own machine, then reads insights out of it."
          : "The file is stored exactly as it arrived. Reading a document for insights needs its text pulled out first, which is not built yet — a note (which is already text) is read straight away."}
      </p>
    </div>
  );
}

/**
 * CAP-7 — the quick note. A note has no file and no audio pipeline: its body is its
 * text, so it goes straight to insight extraction (INS-1). The form is deliberately the
 * whole page — a title and a body, and a date because a relative reference inside the
 * note ("before Friday") resolves against it when it is extracted.
 */
export function WriteNotePage({ capture }: { capture: CaptureRoute }) {
  const { api, refreshRecentSources } = useApp();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sourceDate, setSourceDate] = useState(today());
  const [authoredByMe, setAuthoredByMe] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [added, setAdded] = useState<SourceMaterial | null>(null);

  const save = useCallback(async () => {
    if (body.trim().length === 0) return;

    setSaving(true);
    setProblem(null);

    try {
      const created = await api.createNote({
        title: title.trim() || undefined,
        body,
        sourceDate,
        authoredByMe: authoredByMe || undefined,
      });

      setAdded(created);
      setTitle("");
      setBody("");
      setAuthoredByMe(false);
      // The panel's "Recently added" is the receipt for this, so it must not lag behind.
      refreshRecentSources();
    } catch (cause) {
      setProblem(cause instanceof ApiError ? cause.message : "The note could not be saved.");
    } finally {
      setSaving(false);
    }
  }, [api, authoredByMe, body, refreshRecentSources, sourceDate, title]);

  return (
    <div className="flex max-w-[940px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-wrap items-baseline gap-[var(--space-4)]">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          {capture.label}
        </h1>
        <p className="max-w-[64ch] text-[length:var(--text-md)] text-[var(--text-secondary)]">
          A quick thought or fact. It is kept like any other source and read for insights straight away.
        </p>
      </header>

      {added && <AddedPanel source={added} />}

      <div className="flex flex-col gap-[var(--space-4)] rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
        <div className="flex flex-wrap gap-[var(--space-4)]">
          <Field label="Title (optional)" className="min-w-[240px] flex-[2]">
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={saving}
              placeholder="What this is about"
              className={inputClass}
            />
          </Field>

          <Field label="Note date" className="min-w-[160px] flex-1">
            <input
              type="date"
              value={sourceDate}
              onChange={(event) => setSourceDate(event.target.value)}
              disabled={saving}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label="Note">
          <textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            disabled={saving}
            rows={8}
            placeholder="What happened, what was decided, what you want to remember…"
            className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3 py-2.5 text-[length:var(--text-sm)] leading-relaxed text-[var(--text-primary)] outline-none focus:border-[var(--ring)] disabled:opacity-60"
          />
        </Field>

        <AuthoredByMe checked={authoredByMe} onChange={setAuthoredByMe} disabled={saving} noun="note" />

        {problem && (
          <p
            role="alert"
            className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-lg)] border border-[var(--destructive)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]"
            style={{ background: "color-mix(in oklch, var(--destructive) 10%, transparent)" }}
          >
            <TriangleAlert size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-[var(--destructive)]" />
            {problem}
          </p>
        )}

        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || body.trim().length === 0}
            className={primaryButtonClass}
          >
            <StickyNote size={14} strokeWidth={2.5} />
            {saving ? "Saving…" : "Add note"}
          </button>
        </div>
      </div>

      <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
        A note is stored immutably like any other source and goes straight to insight extraction — there is no audio
        to transcribe.
      </p>
    </div>
  );
}

// ------------------------------------------------------------------ pieces

/**
 * CAP-8 — "this is my own words". Set here, at capture, and never afterwards: what is approved out
 * of the source lands in the world view as the owner's assertion, and a flag that could be flipped
 * later would rewrite whose word an approved belief was. It still goes through extraction and the
 * review queue — the sentence under the box says so, because "pinned" should not read as "skips
 * review".
 */
function AuthoredByMe({
  checked,
  onChange,
  disabled,
  noun,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled: boolean;
  noun: "note" | "document";
}) {
  return (
    <label className="flex cursor-pointer items-start gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] px-[var(--space-4)] py-[var(--space-3)] has-[:checked]:border-[var(--vl-bridge)]">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--vl-bridge)]"
      />
      <span className="flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
          <PenLine size={13} strokeWidth={2} className="text-[var(--vl-bridge)]" aria-hidden />
          Written by me
        </span>
        <span className="text-[length:var(--text-xs)] leading-relaxed text-[var(--text-secondary)]">
          This {noun} is in your own words. You still review what is read out of it, and what you approve lands in
          your world view as yours — pinned, like something typed on a page.
        </span>
      </span>
    </label>
  );
}

function DropZone({
  spec,
  onChoose,
  disabled,
}: {
  spec: NonNullable<CaptureRoute["upload"]>;
  onChoose: (file: File | null) => void;
  disabled: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  return (
    <div
      data-testid="drop-zone"
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        onChoose(event.dataTransfer.files?.[0] ?? null);
      }}
      className={cn(
        "flex flex-col items-center gap-[var(--space-3)] rounded-[var(--radius-xl)] border-[1.5px] border-dashed bg-[var(--surface-glass)] px-[var(--space-6)] py-[var(--space-12)] text-center transition-colors duration-[var(--duration-fast)]",
        over ? "border-[var(--primary)]" : "border-[var(--border-strong)]",
      )}
    >
      <CloudUpload size={32} strokeWidth={2} className="text-[var(--vl-bridge)]" />

      <p className="text-[length:var(--text-lg)] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
        {spec.dropTitle}
      </p>
      <p className="max-w-[46ch] text-[length:var(--text-md)] text-[var(--text-secondary)]">{spec.dropBody}</p>

      <input
        ref={input}
        type="file"
        accept={spec.extensions.join(",")}
        onChange={(event) => onChoose(event.target.files?.[0] ?? null)}
        className="sr-only"
        aria-label="Choose a file"
      />

      <button
        type="button"
        onClick={() => input.current?.click()}
        disabled={disabled}
        className={cn(primaryButtonClass, "mt-[var(--space-2)]")}
      >
        <FolderOpen size={14} strokeWidth={2.5} />
        Browse files
      </button>

      <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">{spec.formatNote}</p>
    </div>
  );
}

function StagedFileCard({
  file,
  kind,
  progress,
  onCancel,
}: {
  file: File;
  kind: "recording" | "document";
  progress: number | null;
  onCancel: () => void;
}) {
  const Icon = kind === "recording" ? FileAudio : FileText;
  const percent = progress === null ? null : Math.round(progress * 100);

  return (
    <div className="flex flex-wrap items-center gap-[var(--space-4)] rounded-[var(--radius-xl)] border-[1.5px] border-dashed border-[var(--border-strong)] bg-[var(--surface-glass)] p-[var(--space-6)]">
      <Icon size={26} strokeWidth={2} className="shrink-0 text-[var(--vl-bridge)]" />

      <div className="flex min-w-[220px] flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-2.5">
          <p className="text-[length:var(--text-md)] font-semibold text-[var(--text-primary)]">{file.name}</p>
          <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
            {formatBytes(file.size)}
            {percent === null ? " · ready" : ` · uploading · ${percent}%`}
          </p>
        </div>

        <div
          role="progressbar"
          aria-label="Upload progress"
          aria-valuenow={percent ?? 0}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-1.5 w-full overflow-hidden rounded-[var(--radius-full)] bg-[var(--surface-2)]"
        >
          <div
            className="h-full rounded-[var(--radius-full)] bg-[var(--primary)] transition-[width] duration-[var(--duration-fast)]"
            style={{ width: `${percent ?? 0}%` }}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={onCancel}
        aria-label={percent === null ? "Remove this file" : "Cancel the upload"}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-muted)] hover:bg-[var(--surface-2)] hover:text-[var(--text-primary)]"
      >
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

/**
 * CAP-3's prompt. It has to carry three things: that this is not new, what the earlier
 * upload is called and when it arrived, and a way to get to it.
 */
function DuplicatePanel({ duplicate, onSkip }: { duplicate: DuplicateSource; onSkip: () => void }) {
  const name = duplicate.title ?? duplicate.originalFilename ?? "an earlier upload";

  return (
    <div
      role="alert"
      className="flex flex-wrap items-start gap-3 rounded-[var(--radius-xl)] border border-[var(--warning)] p-[var(--space-4)]"
      style={{ background: "color-mix(in oklch, var(--warning) 12%, transparent)" }}
    >
      <CopyCheck size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-[var(--warning)]" />

      <div className="min-w-[240px] flex-1">
        <p className="font-semibold text-[var(--text-primary)]">You already uploaded this file</p>
        <p className="mt-1 text-[length:var(--text-sm)] leading-relaxed text-[var(--text-secondary)]">
          {duplicate.uploadedFilename} has the same content hash as{" "}
          <strong className="text-[var(--text-primary)]">{name}</strong>, uploaded{" "}
          {formatDate(duplicate.uploadedAt)}.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button type="button" onClick={onSkip} className={ghostButtonClass}>
          <X size={13} strokeWidth={2.5} />
          Skip this file
        </button>

        <Link to={`/sources/${duplicate.sourceMaterialId}`} className={outlineButtonClass}>
          <ArrowUpRight size={13} strokeWidth={2.5} />
          Open the original
        </Link>
      </div>
    </div>
  );
}

function AddedPanel({ source }: { source: SourceMaterial }) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-[var(--radius-xl)] border border-[var(--success)] p-[var(--space-4)]"
      style={{ background: "color-mix(in oklch, var(--success) 12%, transparent)" }}
    >
      <CircleCheckBig size={16} strokeWidth={2} className="shrink-0 text-[var(--success)]" />

      <p className="min-w-[240px] flex-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        <strong className="text-[var(--text-primary)]">{source.title ?? source.originalFilename}</strong> was
        added. Drop in the next one whenever you like.
      </p>

      <Link to={`/sources/${source.id}`} className={outlineButtonClass}>
        <ArrowUpRight size={13} strokeWidth={2.5} />
        Open it
      </Link>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">{label}</span>
      {children}
    </label>
  );
}

/**
 * The formats people actually try are the ones that were ruled out on purpose, and
 * "not accepted" reads like a bug unless it says why.
 *
 * Said in both places rather than only at the Server, because this is the message a
 * dropped file gets, and the Server never sees that file at all. The two wordings are
 * kept identical on purpose — a rule explained two different ways reads as two rules.
 */
function explainOmission(extension: string): string {
  if ([".mp4", ".mkv", ".mov", ".avi", ".webm"].includes(extension))
    return " Video is deliberately out of scope — upload the audio track instead.";
  if (extension === ".pdf") return " PDF is deliberately out of scope.";
  if ([".html", ".htm"].includes(extension))
    return " HTML is deliberately out of scope — save it as markdown or text.";

  return "";
}

/** One to twelve, which covers a meeting anybody actually records; past that the
 * diarizer's own answer is as good as a guess. */
const SPEAKER_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const inputClass =
  "h-9 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)] disabled:opacity-60";

// `<select>` shares the glass background above, but the browser's native options list
// renders outside that translucent surface — so without an opaque background of its
// own it falls back to a default one, and text-primary (near-white in dark mode) reads
// as white-on-white.
const optionClass = "bg-[var(--surface-2)] text-[var(--text-primary)]";

const primaryButtonClass =
  "inline-flex h-9 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50";

const ghostButtonClass =
  "inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] transition hover:bg-[var(--surface-2)] hover:text-[var(--text-primary)]";

const outlineButtonClass =
  "inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] transition hover:bg-[var(--surface-2)]";
